import React from 'react';
import { Handle, Position } from 'reactflow';
import { Plus } from 'lucide-react';
import { AutomationTriggerType } from '@frontend/app/types/globalTypes';
import { automationTriggerDisplayName } from '@frontend/app/containers/Projects/AutomationConfig/constants';
import { Button } from '@/shadcn/components/ui/button';
import { FlowNodeData } from '../types';
import { cn } from '@/shadcn/lib/utils';
import { getTriggerConfig } from '../utils/triggerConstants';

interface TriggerNodeV2Props {
  data: FlowNodeData;
}

const getTriggerDescription = (triggerType?: string) => {
  // Use the standard V1 automation trigger display names for consistency
  if (triggerType && automationTriggerDisplayName[triggerType as AutomationTriggerType]) {
    return automationTriggerDisplayName[triggerType as AutomationTriggerType];
  }
  return 'This event starts the automation';
};

export const TriggerNodeV2: React.FC<TriggerNodeV2Props> = ({ data }) => {
  const {
    nodeId,
    metadata,
    isEditing,
    isSelected,
    onNodeClick,
  } = data;

  const triggerType = metadata?.triggerType || 'PROGRAM_APPLIED';
  const triggerConfig = getTriggerConfig(triggerType);

  const handleNodeClick = () => {
    onNodeClick?.(nodeId);
  };

  return (
    <div className="relative">
      {/* Main node container - Figma style */}
      <div
        className={cn(
          'w-64 bg-white border border-gray-200 rounded-2xl shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md hover:border-gray-300',
          isSelected && 'ring-2 ring-blue-500 ring-offset-2 border-blue-300'
        )}
        onClick={handleNodeClick}
      >
        <div className="p-4">
          {/* Header with icon and title */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <div className="text-white">
                  <triggerConfig.icon className="h-4 w-4" />
                </div>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 text-sm">{triggerConfig.label}</h3>
              </div>
            </div>
          </div>

          {/* Content area */}
          <div className="space-y-3">
            <div className="bg-blue-50 rounded-lg px-3 py-2">
              <span className="text-sm text-blue-700 font-medium">Trigger Event</span>
              <p className="text-xs text-blue-600 mt-1">
                {getTriggerDescription(triggerType)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        className="w-3 h-3 bg-gray-400 border-2 border-white"
      />

      {/* Add button positioned at bottom center */}
      {isEditing && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-4 z-20">
          <Button
            variant="outline"
            size="sm"
            className="w-8 h-8 p-0 rounded-full bg-white border border-gray-300 hover:border-blue-400 hover:bg-blue-50 shadow-md"
            onClick={(e) => {
              e.stopPropagation(); // Prevent node selection
              const rect = e.currentTarget.getBoundingClientRect();
              data.onShowOptionBar?.(nodeId, {
                x: rect.left + rect.width / 2,
                y: rect.top
              });
            }}
          >
            <Plus className="h-4 w-4 text-gray-600" />
          </Button>
        </div>
      )}
    </div>
  );
};
